// Crew Settings Department Tab - department management table (indigo theme, Figma design)
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import { MoreHorizontal, Plus, Search } from 'lucide-react';

import { ErrorToast, SuccessToast } from 'components';
import { DeleteModal } from 'features';
import DepartmentService from 'api/department';
import EmployeeService from 'api/employees';
import DepartmentManagement_Modal from './crew-department-modal';
import { setUserRights, setUserDepartments } from 'redux/generalState.actions';

// ---- helpers ----

const StatusBadge = ({ isDeleted }) =>
  isDeleted ? (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-red-100 tw-text-red-700">
      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-red-500" />
      ARCHIVED
    </span>
  ) : (
    <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-px-2.5 tw-py-0.5 tw-rounded-full tw-text-xs tw-font-medium tw-bg-green-100 tw-text-green-700">
      <span className="tw-w-1.5 tw-h-1.5 tw-rounded-full tw-bg-green-500" />
      ACTIVE
    </span>
  );

import moment from 'moment';

// ---- action menu ----
const ActionMenu = ({ dept, onEdit, onUnlink, onDelete, onRestore, canEdit }) => {
  const [open, setOpen] = useState(false);

  if (!canEdit) return null;

  return (
    <div className="tw-relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="tw-p-1 tw-rounded tw-border-0 tw-bg-transparent tw-cursor-pointer hover:tw-bg-gray-100 tw-transition-colors"
      >
        <MoreHorizontal className="tw-w-4 tw-h-4 tw-text-gray-500" />
      </button>
      {open && (
        <>
          <div className="tw-fixed tw-inset-0 tw-z-10" onClick={() => setOpen(false)} />
          <div className="tw-absolute tw-right-0 tw-top-7 tw-z-20 tw-bg-white tw-rounded-lg tw-shadow-lg tw-border tw-border-gray-200 tw-py-1 tw-min-w-36">
            <button
              onClick={() => { onEdit(); setOpen(false); }}
              className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
            >
              Edit
            </button>
            <button
              onClick={() => { onUnlink(); setOpen(false); }}
              className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
            >
              Remove all members
            </button>
            {dept.isDeleted ? (
              <button
                onClick={() => { onRestore(); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-gray-700 hover:tw-bg-gray-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
              >
                Restore
              </button>
            ) : (
              <button
                onClick={() => { onDelete(); setOpen(false); }}
                className="tw-w-full tw-text-left tw-px-4 tw-py-2 tw-text-sm tw-text-red-600 hover:tw-bg-red-50 tw-border-0 tw-bg-transparent tw-cursor-pointer"
              >
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ---- main component ----

const CrewSettingsDepartmentTab = () => {
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.general.user);
  const writeDepts = useSelector((state) => state.general?.userRightsObj?.departments?.writeDepts);

  const [departments, setDepartments] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editDept, setEditDept] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ open: false, action: null, deptId: null });
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => { fetchDepartments(); }, []);
  useEffect(() => { if (!isModalOpen) setEditDept(null); }, [isModalOpen]);

  const fetchDepartments = async (search = '') => {
    try {
      setIsSearching(true);
      const { data: { data } } = await DepartmentService.getAll({ filters: {}, search });
      dispatch(setUserDepartments(data));
      setDepartments(data);
    } catch (error) {
      dispatch(setUserDepartments([]));
      dispatch(setUserRights({}));
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = useCallback(debounce((val) => fetchDepartments(val), 500), []);

  const onSearchChange = (val) => { setSearchValue(val); handleSearch(val); };

  const openDeleteModal = (deptId, action) =>
    setDeleteModal({ open: true, action, deptId });

  const closeDeleteModal = () =>
    setDeleteModal({ open: false, action: null, deptId: null });

  const onUnlink = async () => {
    try {
      await EmployeeService.removeDepartmentFromAll(deleteModal.deptId);
      await fetchDepartments();
      toast.success(<SuccessToast message="Removed all users from department" />);
      closeDeleteModal();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const onDelete = async () => {
    try {
      setIsDeleting(true);
      await DepartmentService.delete(deleteModal.deptId);
      await fetchDepartments();
      closeDeleteModal();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    } finally {
      setIsDeleting(false);
    }
  };

  const onRestore = async (dept) => {
    try {
      await DepartmentService.update(dept._id, { ...dept, isDeleted: false });
      await fetchDepartments();
    } catch (error) {
      toast.error(<ErrorToast error={error} />);
    }
  };

  const modalActions = {
    unlink: {
      header: 'Are you sure you want to remove all members?',
      content: 'This will remove all members from this department. This action cannot be undone.',
      action: onUnlink,
      buttonText: 'Remove',
    },
    delete: {
      header: 'Are you sure you want to delete this department?',
      content: 'Deleted departments can later be restored.',
      action: onDelete,
      buttonText: 'Delete',
    },
  };

  const isAdmin = userInfo?.metadata?.role === 'admin';

  return (
    <div className="tw-p-6">
      {/* Create/edit modal (reuse existing) */}
      {isModalOpen && (
        <DepartmentManagement_Modal
          toggleModal={setIsModalOpen}
          isModalToggled={isModalOpen}
          fetchData={fetchDepartments}
          editDept={editDept}
        />
      )}

      {/* Delete/unlink confirm modal */}
      {deleteModal.open && modalActions[deleteModal.action] && (
        <DeleteModal
          toggleModal={closeDeleteModal}
          isModalToggled={deleteModal.open}
          header={modalActions[deleteModal.action].header}
          content={<span>{modalActions[deleteModal.action].content}</span>}
          onDelete={modalActions[deleteModal.action].action}
          buttonText={modalActions[deleteModal.action].buttonText}
          isSaving={isDeleting}
        />
      )}

      {/* Toolbar */}
      <div className="tw-flex tw-items-center tw-justify-between tw-mb-5 tw-gap-3">
        <div className="tw-relative tw-flex-1 tw-max-w-xs">
          <Search className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-w-4 tw-h-4 tw-text-gray-400 tw-pointer-events-none" />
          <input
            type="text"
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            placeholder="Search departments..."
            className="tw-w-full tw-pl-9 tw-pr-4 tw-py-2 tw-text-sm tw-border tw-border-gray-300 tw-rounded-lg tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500 focus:tw-border-indigo-500"
          />
        </div>
        {isAdmin && (
          <button
            onClick={() => setIsModalOpen(true)}
            className="tw-flex tw-items-center tw-gap-2 tw-px-4 tw-py-2 tw-bg-indigo-700 tw-text-white tw-text-sm tw-font-medium tw-rounded-lg tw-border-0 tw-cursor-pointer hover:tw-bg-indigo-800 tw-transition-colors tw-whitespace-nowrap"
          >
            <Plus className="tw-w-4 tw-h-4" />
            Create new department
          </button>
        )}
      </div>

      {/* Table */}
      <div className="tw-bg-white tw-rounded-xl tw-border tw-border-gray-200 tw-overflow-hidden">
        <div className="tw-overflow-x-auto">
          <table className="tw-w-full tw-text-left tw-border-collapse">
            <thead>
              <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200 tw-text-xs tw-uppercase tw-tracking-wider tw-text-gray-500 tw-font-semibold">
                <th className="tw-p-4">Department</th>
                <th className="tw-p-4">#Tag</th>
                <th className="tw-p-4">Status</th>
                <th className="tw-p-4">Active since</th>
                <th className="tw-p-4">Archived</th>
                <th className="tw-p-4 tw-text-center">Action</th>
              </tr>
            </thead>
            <tbody className="tw-divide-y tw-divide-gray-100">
              {isSearching ? (
                <tr>
                  <td colSpan={6} className="tw-p-8 tw-text-center">
                    <div className="tw-w-6 tw-h-6 tw-border-4 tw-border-indigo-600 tw-border-t-transparent tw-rounded-full tw-animate-spin tw-mx-auto" />
                  </td>
                </tr>
              ) : departments.length === 0 ? (
                <tr>
                  <td colSpan={6} className="tw-p-8 tw-text-center tw-text-sm tw-text-gray-400">
                    No departments found.
                  </td>
                </tr>
              ) : (
                departments.map((dept) => {
                  const canEdit = writeDepts?.includes(dept._id) || isAdmin;
                  return (
                    <tr key={dept._id} className="hover:tw-bg-gray-50 tw-transition-colors">
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-font-semibold tw-text-gray-900">{dept.name}</span>
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-text-gray-600">
                          {dept?.tags?.length ? dept.tags.join(', ') : '—'}
                        </span>
                      </td>
                      <td className="tw-p-4">
                        <StatusBadge isDeleted={dept.isDeleted} />
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-text-gray-600">
                          {moment(dept.createdAt).format('DD MMM YYYY')}
                        </span>
                      </td>
                      <td className="tw-p-4">
                        <span className="tw-text-sm tw-text-gray-600">
                          {dept.isDeleted && dept.archivedAt
                            ? moment(dept.archivedAt || dept.updatedAt).format('DD MMM YYYY')
                            : '—'}
                        </span>
                      </td>
                      <td className="tw-p-4 tw-text-center">
                        <ActionMenu
                          dept={dept}
                          canEdit={canEdit}
                          onEdit={() => { setEditDept(dept); setIsModalOpen(true); }}
                          onUnlink={() => openDeleteModal(dept._id, 'unlink')}
                          onDelete={() => openDeleteModal(dept._id, 'delete')}
                          onRestore={() => onRestore(dept)}
                        />
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default CrewSettingsDepartmentTab;
